<template>
  <article class="solution-entry" :class="`solution-entry--${status.tone}`" :aria-label="$t('解决方案')">
    <div class="solution-entry__body">
      <header class="solution-entry__header">
        <span class="solution-entry__status" :class="`solution-entry__status--${status.tone}`">{{ status.label }}</span>
        <span v-if="latest" class="solution-entry__latest">{{ $t("最新方案") }}</span>
      </header>
      <div class="solution-entry__meta solution-entry__meta--submission" :aria-label="$t('方案提交记录')">
        <span class="solution-entry__actor">
          <span>{{ (solution.solver && solution.solver.name) || $t("未设置解决人") }}</span>
          <span class="solution-entry__verb">{{ $t("提交") }}</span>
        </span>
        <span v-if="solution.answerDate" class="solution-entry__timestamp">{{ solution.answerDate }}</span>
      </div>
      <p v-if="solution.description" class="solution-entry__description">{{ solution.description }}</p>
      <div v-if="images.length" class="solution-entry__images">
        <div v-for="(url, index) in images.slice(0, 4)" :key="index" class="solution-entry__image">
          <el-image :src="url" :preview-src-list="images" fit="cover" :alt="$t('解决方案图片')" lazy />
          <span v-if="index === 3 && images.length > 4" class="solution-entry__more">+{{ images.length - 4 }}</span>
        </div>
      </div>
      <p v-if="!solution.description && !images.length" class="solution-entry__empty">{{ $t("暂无方案内容") }}</p>
      <p v-if="status.tone === 'rejected' && solution.rejectionReason" class="solution-entry__reason">
        <span>{{ $t("不通过原因") }}</span>{{ solution.rejectionReason }}
      </p>
      <div v-if="solution.result !== 0 && (solution.checkDate || reviewerName || solution.rating)"
        class="solution-entry__meta solution-entry__meta--review" :aria-label="$t('方案验收记录')">
        <span v-if="reviewerName || solution.checkDate" class="solution-entry__actor">
          <span v-if="reviewerName">{{ reviewerName }}</span>
          <span class="solution-entry__verb">{{ $t("验收") }}</span>
        </span>
        <span v-if="solution.checkDate" class="solution-entry__timestamp">{{ solution.checkDate }}</span>
        <span v-if="solution.result === 3 && solution.rating" class="solution-entry__rating" :title="$t('评级：{p0} / 5', { p0: solution.rating })">
          <i class="el-icon-star-off" aria-hidden="true"></i>{{ solution.rating }} / 5
        </span>
      </div>
    </div>
    <issue-card-actions mode="review" :issue="issue" :solution="solution" :user="user"
      :can-approve="canApprove" :locked="reviewBusy"
      @busy="$emit('busy', $event)" @changed="$emit('changed', $event)" />
  </article>
</template>

<script>
import IssueCardActions from './IssueCardActions.vue';
import { solutionStatus } from '@/utils/issueSolutions';

export default {
  name: 'IssueSolutionItem',
  components: { IssueCardActions },
  props: {
    solution: { type: Object, required: true },
    issue: { type: Object, required: true },
    user: { type: Object, required: true },
    latest: Boolean,
    canApprove: Boolean,
    reviewBusy: Boolean,
  },
  computed: {
    status() { return solutionStatus(this.solution); },
    images() { return (this.solution.images || []).filter(Boolean); },
    reviewerName() { return (this.solution.reviewer && this.solution.reviewer.name) || ''; },
  },
};
</script>

<style scoped>
.solution-entry { position: relative; min-width: 0; }
.solution-entry::before { content: ''; position: absolute; z-index: 1; top: 5px; left: calc(var(--solution-rail-left, -21px) - 4px); width: 9px; height: 9px; box-sizing: border-box; border: 1px solid var(--surface-color); border-radius: 50%; background: var(--text-muted); pointer-events: none; }
.solution-entry::after { content: ''; position: absolute; top: 0; bottom: -20px; left: var(--solution-rail-left, -21px); width: 1px; background: var(--border-color); pointer-events: none; }
/* Stop at the status dot unless the list continues into a solution composer. */
.solution-entry:last-child::after { bottom: var(--solution-tail-bottom, auto); height: var(--solution-tail-height, 10px); }
.solution-entry--pending::before { background: var(--status-warning-text); }
.solution-entry--rejected::before { background: var(--status-danger-text); }
.solution-entry--accepted::before { background: var(--status-success-text); }
.solution-entry__body { padding: 0; }
.solution-entry__header { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
.solution-entry__status { color: var(--text-muted); font-size: 12px; font-weight: 600; line-height: 20px; }
.solution-entry__status--pending { color: var(--status-warning-text); }
.solution-entry__status--rejected { color: var(--status-danger-text); }
.solution-entry__status--accepted { color: var(--status-success-text); }
.solution-entry__latest { color: var(--text-muted); font-size: 11px; }
.solution-entry__description { margin: 10px 0 0; color: var(--text-regular); font-size: 13px; line-height: 1.7; white-space: pre-wrap; overflow-wrap: anywhere; }
.solution-entry__images { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 6px; margin-top: 10px; }
.solution-entry__image { position: relative; padding-top: 100%; overflow: hidden; border: 1px solid var(--border-light); border-radius: 7px; }
.solution-entry__image .el-image { position: absolute; inset: 0; width: 100%; height: 100%; cursor: zoom-in; }
.solution-entry__more { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; pointer-events: none; background: rgba(8, 15, 25, 0.58); color: #fff; font-size: 15px; }
.solution-entry__reason { margin: 10px 0 0; color: var(--text-regular); font-size: 12px; line-height: 1.6; white-space: pre-wrap; overflow-wrap: anywhere; }
.solution-entry__reason span { display: block; margin-bottom: 3px; color: var(--status-danger-text); font-weight: 600; }
.solution-entry__meta { display: flex; align-items: baseline; flex-wrap: wrap; gap: 4px 10px; margin-top: 10px; color: var(--text-muted); font-size: 11px; line-height: 1.65; overflow-wrap: anywhere; }
.solution-entry__actor { display: inline-flex; align-items: baseline; gap: 5px; min-width: 0; max-width: 100%; color: var(--text-regular); }
.solution-entry__verb { flex-shrink: 0; color: var(--text-muted); }
.solution-entry__timestamp { min-width: 0; }
.solution-entry__timestamp::before { content: '·'; margin-right: 10px; }
.solution-entry__rating { display: inline-flex; align-items: center; gap: 4px; }
.solution-entry__empty { margin: 10px 0 0; color: var(--text-muted); font-size: 12px; }
.solution-entry__meta--submission { margin-top: 4px; }
</style>
